import React, { useEffect, useState, useRef } from "react";
import { hatch } from "ldrs";
import { Card } from "../../../components/layouts/Card";
import BarChart from "./visualizations/BarChart";
import TreeMap from "./visualizations/TreeMap";
import HorizontalStackedBarChart from "./visualizations/HorizontalStackedBarChart";
import { QUESTION_TYPES } from "../config/questionConfig";
import {
  fetchClosedEndedData,
  fetchCategorizedOpenEndedData,
  fetchMatrixData,
} from "../services/api";
import { useSelector } from "react-redux";
import { selectCivicPulseState } from "../../../store/civicPulseSlice";
import useCancelableRequest from "../hooks/useCancelableRequest";

// Register the loading animation
hatch.register();

// Subcomponent for closed-ended questions
const ClosedEndedQuestion = ({ question, activeFilters }) => {
  const { data, loading } = useCancelableRequest(
    fetchClosedEndedData,
    [question.id, activeFilters],
    [question.id, activeFilters]
  );
  // Format for BarChart
  const formattedData = data
    ? {
        responses: data.reduce(
          (acc, d) => ({
            ...acc,
            [d.response]: d.weighted_percent / 100,
          }),
          {}
        ),
      }
    : null;

  return (
    <div className={`col-span-${question.gridProps.colSpan}`}>
      <Card>
        <h2 className="text-lg mb-4">{question.title}</h2>
        {question.subtitle && (
          <p className="text-slate-600 italic text-sm mb-6">
            {question.subtitle}
          </p>
        )}
        <div className={`h-[${question.chartProps.height}px]`}>
          {loading || !formattedData ? (
            <div className="flex justify-center items-center min-h-[200px]">
              <l-hatch size="32" color="#334155"></l-hatch>
            </div>
          ) : (
            <BarChart
              data={formattedData}
              width={question.chartProps.width}
              height={question.chartProps.height}
              margin={
                question.chartProps.margin || {
                  top: 20,
                  right: 80,
                  bottom: 20,
                  left: 325,
                }
              }
              colors={["#157787"]}
            />
          )}
        </div>
      </Card>
    </div>
  );
};

// Subcomponent for open-ended questions
const OpenEndedQuestion = ({
  question,
  activeFilters,
  onCategoryClick,
  onVisibilityChange,
}) => {
  const { selectedCategory } = useSelector(selectCivicPulseState);
  const { data, loading } = useCancelableRequest(
    fetchCategorizedOpenEndedData,
    [question.id, activeFilters],
    [question.id, activeFilters]
  );
  // Format for TreeMap
  const formattedData = data
    ? {
        name: "root",
        children: data.map((item) => ({
          name: item.tag,
          value: item.weighted_percent,
        })),
      }
    : null;

  useEffect(() => {
    if (!loading && formattedData && onVisibilityChange)
      onVisibilityChange(true);
    // eslint-disable-next-line
  }, [loading, formattedData]);

  if (loading || !formattedData) {
    return (
      <div className="col-span-12">
        <Card>
          <h2 className="text-lg font-semibold mb-4">{question.title}</h2>
          <div className="flex justify-center items-center min-h-[200px]">
            <l-hatch size="32" color="#334155"></l-hatch>
          </div>
        </Card>
      </div>
    );
  }

  const handleNodeClick = (node) => {
    onCategoryClick(node.name, question.id, question.title);
  };

  return (
    <div className="col-span-12">
      <Card>
        <h2 className="text-lg mb-4">{question.title}</h2>
        <div className="h-[300px] flex justify-center items-center">
          <TreeMap
            data={formattedData}
            width={600}
            height={300}
            onNodeClick={handleNodeClick}
            showTooltip={false}
            maxNodes={8}
            selectedCategory={selectedCategory}
            questionId={question.id}
          />
        </div>
      </Card>
    </div>
  );
};

// Subcomponent for matrix questions
const MatrixQuestion = ({ question, activeFilters }) => {
  // data is { data, categories }
  const { data, loading } = useCancelableRequest(
    fetchMatrixData,
    [
      question.questionIds,
      question.rowLabels,
      activeFilters,
      question.categories,
    ],
    [
      question.questionIds,
      question.rowLabels,
      activeFilters,
      question.categories,
    ]
  );

  return (
    <div className={`col-span-${question.gridProps.colSpan}`}>
      <Card>
        <h2 className="text-lg mb-4">{question.title}</h2>
        {loading || !data ? (
          <div className="min-h-[200px] flex justify-center items-center w-full">
            <l-hatch size="32" color="#334155"></l-hatch>
          </div>
        ) : (
          <div className="overflow-x-auto w-full">
            <div
              className={`h-[${question.chartProps.height}px] min-w-[${question.chartProps.width}px] flex justify-center items-center`}
            >
              <HorizontalStackedBarChart
                data={data.data}
                width={question.chartProps.width}
                height={question.chartProps.height}
                colors={question.chartProps.colors}
                legendHeight={question.chartProps.legendHeight}
                showLegend={true}
                showPercentages={true}
              />
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

const QuestionSection = ({
  section,
  onCategoryClick,
  onVisibilityChange,
  activeFilters,
}) => {
  return (
    <div className="col-span-12">
      {section.title && (
        <h2 className="text-2xl mb-6 underline decoration-mblue-700 underline-offset-4 decoration-4">
          {section.title}
        </h2>
      )}
      <div className="grid grid-cols-12 gap-6">
        {section.questions.map((question) => {
          if (question.type === QUESTION_TYPES.CLOSED_ENDED) {
            return (
              <ClosedEndedQuestion
                key={question.id}
                question={question}
                activeFilters={activeFilters}
              />
            );
          } else if (question.type === QUESTION_TYPES.OPEN_ENDED) {
            return (
              <OpenEndedQuestion
                key={question.id}
                question={question}
                activeFilters={activeFilters}
                onCategoryClick={onCategoryClick}
                onVisibilityChange={onVisibilityChange}
              />
            );
          } else if (question.type === QUESTION_TYPES.MATRIX) {
            return (
              <MatrixQuestion
                key={question.id}
                question={question}
                activeFilters={activeFilters}
              />
            );
          }
          return null;
        })}
      </div>
    </div>
  );
};

export default QuestionSection;
